import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';

const AIInterview = ({ interviewId, applicationId, candidateInfo, onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isRecording, setIsRecording] = useState(false);
  const [isVideoRecording, setIsVideoRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [answerType, setAnswerType] = useState('text');
  const [videoRecordingComplete, setVideoRecordingComplete] = useState(false);
  
  // Proctoring states
  const [tabViolationCount, setTabViolationCount] = useState(0);
  const [copyViolationCount, setCopyViolationCount] = useState(0);
  const [fullscreenViolationCount, setFullscreenViolationCount] = useState(0);
  const [showLockdownWarning, setShowLockdownWarning] = useState(false);
  const [lockdownMessage, setLockdownMessage] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [isTerminated, setIsTerminated] = useState(false);

  const mediaRecorderRef = useRef(null);
  const videoMediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const videoChunksRef = useRef([]);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fullscreenAttempts = useRef(0);
  
  const maxTabViolations = 3;
  const maxFullscreenViolations = 5;
  const maxCopyViolations = 3;
  const [videoDuration, setVideoDuration] = useState(0);

  useEffect(() => {
    loadAIQuestions();
    setTimeout(() => enableCamera(), 500);
    enforceFullscreen();
    setupProctoring();

    const fullscreenInterval = setInterval(() => {

  if (
    !document.fullscreenElement &&
    !submitted &&
    !submitting &&
    !isTerminated
  ) {

    handleFullscreenChange();
  }

}, 2000);
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
      if (videoMediaRecorderRef.current && isVideoRecording) {
        videoMediaRecorderRef.current.stop();
      }
      stopCamera();
      removeProctoring();
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
    };
  }, []);

  const terminateInterview = () => {

  setIsTerminated(true);

  setLockdownMessage(
    '❌ Interview terminated due to multiple fullscreen violations.'
  );

  setShowLockdownWarning(true);

  if (timerRef.current) {
    clearInterval(timerRef.current);
  }

  stopCamera();

  setTimeout(() => {
    window.location.href = '/terminated';
  }, 3000);
};

  const enableCamera = async () => {
    try {
      console.log('📷 Starting camera...');
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      const constraints = {
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        },
        audio: true
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(e => console.log('Play error:', e));
        };
      }
      
      setCameraActive(true);
      setCameraError(null);
      console.log('✅ Camera started successfully');
      
    } catch (error) {
      console.error('❌ Camera error:', error);
      setCameraActive(false);
      setCameraError(error.message || 'Camera access denied');
      setLockdownMessage('Camera access is required for the AI Interview. Please allow camera access and refresh the page.');
      setShowLockdownWarning(true);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  const enforceFullscreen = async () => {
    try {
      const docEl = document.documentElement;
      if (docEl.requestFullscreen) {
        await docEl.requestFullscreen();
      } else if (docEl.webkitRequestFullscreen) {
        await docEl.webkitRequestFullscreen();
      } else if (docEl.msRequestFullscreen) {
        await docEl.msRequestFullscreen();
      }
    } catch (error) {
      console.log('Fullscreen request failed:', error);
    }
  };

  const setupProctoring = () => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    document.addEventListener('copy', preventCopyPaste);
    document.addEventListener('cut', preventCopyPaste);
    document.addEventListener('paste', preventCopyPaste);
    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('keydown', preventKeyboardShortcuts);
    window.addEventListener('blur', handleWindowBlur);
  };

  const removeProctoring = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    document.removeEventListener('copy', preventCopyPaste);
    document.removeEventListener('cut', preventCopyPaste);
    document.removeEventListener('paste', preventCopyPaste);
    document.removeEventListener('contextmenu', preventContextMenu);
    document.removeEventListener('keydown', preventKeyboardShortcuts);
    window.removeEventListener('blur', handleWindowBlur);
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      const newCount = tabViolationCount + 1;
      setTabViolationCount(newCount);
      setLockdownMessage(`⚠️ Tab switching is NOT allowed! Violation ${newCount}/${maxTabViolations}`);
      setShowLockdownWarning(true);
      
      if (newCount >= maxTabViolations) {
        setLockdownMessage(`Multiple violations detected. Submitting interview automatically.`);
        setTimeout(() => handleAutoSubmit(), 2000);
      } else {
        setTimeout(() => setShowLockdownWarning(false), 3000);
      }
    }
  };

const handleFullscreenChange = () => {

  const isFS = !!document.fullscreenElement;

  setIsFullscreen(isFS);

  // ✅ exited fullscreen
  if (!isFS && !submitted && !submitting && !isTerminated) {

    setFullscreenViolationCount((prev) => {

      const updatedCount = prev + 1;

      console.log(
        `❌ Fullscreen Warning ${updatedCount}/${maxFullscreenViolations}`
      );

      // ✅ warning popup
      setLockdownMessage(
        `⚠️ Fullscreen mode is required! Warning ${updatedCount}/${maxFullscreenViolations}`
      );

      setShowLockdownWarning(true);

      // ✅ auto re-enter fullscreen
      setTimeout(() => {
        enforceFullscreen();
      }, 800);

      // ✅ hide warning
      setTimeout(() => {
        setShowLockdownWarning(false);
      }, 3000);

      // ✅ terminate after limit
      if (updatedCount >= maxFullscreenViolations) {

        setLockdownMessage(
          `❌ Too many fullscreen violations. Interview terminated.`
        );

        setShowLockdownWarning(true);

        setTimeout(() => {
          terminateInterview();
        }, 2000);
      }

      return updatedCount;
    });

  } else {
    console.log('✅ Fullscreen active');
  }
};

  const handleWindowBlur = () => {
    window.focus();
    if (!document.hidden) {
      setLockdownMessage(`⚠️ Please stay focused on the interview window!`);
      setShowLockdownWarning(true);
      setTimeout(() => setShowLockdownWarning(false), 2000);
    }
  };

  const preventCopyPaste = (e) => {
    e.preventDefault();
    const newCount = copyViolationCount + 1;
    setCopyViolationCount(newCount);
    setLockdownMessage(`⚠️ Copy/Paste is not allowed! Violation ${newCount}/${maxCopyViolations}`);
    setShowLockdownWarning(true);
    
    if (newCount >= maxCopyViolations) {
      setTimeout(() => handleAutoSubmit(), 2000);
    } else {
      setTimeout(() => setShowLockdownWarning(false), 2000);
    }
    return false;
  };

  const preventContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  const preventKeyboardShortcuts = (e) => {

    if (e.key === 'Escape' || e.keyCode === 27) {

  e.preventDefault();
  e.stopPropagation();

  handleFullscreenChange();

  return false;
}
    if (
      (e.ctrlKey && (e.key === 'c' || e.key === 'C' || 
                     e.key === 'v' || e.key === 'V' ||
                     e.key === 'x' || e.key === 'X' ||
                     e.key === 's' || e.key === 'S' ||
                     e.key === 'p' || e.key === 'P' ||
                     e.key === 'u' || e.key === 'U')) ||
      e.key === 'F12' ||
      (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'C'))
    ) {
      e.preventDefault();
      const newCount = copyViolationCount + 1;
      setCopyViolationCount(newCount);
      setLockdownMessage(`⚠️ Keyboard shortcuts are disabled!`);
      setShowLockdownWarning(true);
      setTimeout(() => setShowLockdownWarning(false), 1500);
      return false;
    }
  };

  const loadAIQuestions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await axios.get(
        `${API_BASE_URL}/api/ai-interview/questions/${interviewId}`,
        {
          params: {
            applicationId: applicationId,
            candidateEmail: candidateInfo.email
          }
        }
      );
      
      if (response.data.success) {
        const questionsData = response.data.data.questions;
        const duration = response.data.data.duration || (questionsData.length * 2);
        
        // Debug: Log questions to see options
        console.log('📋 Received questions:', questionsData);
        questionsData.forEach((q, idx) => {
          console.log(`Q${idx+1}: Type=${q.type}, Has options=${!!q.options}, Options count=${q.options?.length || 0}`);
          if (q.type === 'mcq') {
            console.log(`  Options:`, q.options);
          }
        });
        
        // Add communication question at the end
        const communicationQuestion = {
          _id: "communication_video",
          question: "Please introduce yourself and tell us about your professional background, skills, and why you're interested in this position. (2 minutes video response)",
          type: "video",
          difficulty: "Communication",
          timeLimit: 120,
          isCommunication: true
        };
        
        const allQuestions = [...questionsData, communicationQuestion];
        setQuestions(allQuestions);
        setTimeLeft(duration * 60 + 120);
        startTimer();
      } else {
        setError(response.data.message || 'Failed to load questions');
      }
    } catch (error) {
      console.error('Error loading AI questions:', error);
      setError(error.response?.data?.message || 'Failed to load AI interview questions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
      
      if (isVideoRecording) {
        setVideoDuration(prev => prev + 1);
      }
    }, 1000);
  };

  const handleAutoSubmit = () => {
    if (!submitted && !submitting && questions.length > 0) {
      if (isVideoRecording) {
        stopVideoRecording();
      }
      handleSubmit();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startVideoRecording = async () => {
    try {
      if (!streamRef.current) {
        await enableCamera();
      }
      
      if (!streamRef.current) {
        alert('Camera not available. Please allow camera access.');
        return;
      }
      
      const videoStream = streamRef.current.clone();
      const videoRecorder = new MediaRecorder(videoStream, {
        mimeType: 'video/webm'
      });
      
      videoMediaRecorderRef.current = videoRecorder;
      videoChunksRef.current = [];
      
      videoRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunksRef.current.push(event.data);
        }
      };
      
      videoRecorder.onstop = async () => {
        const videoBlob = new Blob(videoChunksRef.current, { type: 'video/webm' });
        const videoUrl = URL.createObjectURL(videoBlob);
        
        const reader = new FileReader();
        reader.readAsDataURL(videoBlob);
        reader.onloadend = () => {
          const base64Video = reader.result;
          setAnswers(prev => ({
            ...prev,
            communication_video: {
              text: '',
              video: base64Video,
              videoUrl: videoUrl,
              duration: videoDuration
            }
          }));
          setVideoRecordingComplete(true);
        };
        
        videoStream.getTracks().forEach(track => track.stop());
        setIsVideoRecording(false);
        
        setTimeout(() => {
          setShowConfirmSubmit(true);
        }, 1000);
      };
      
      videoRecorder.start();
      setIsVideoRecording(true);
      setVideoDuration(0);
      
      setTimeout(() => {
        if (videoMediaRecorderRef.current && isVideoRecording) {
          stopVideoRecording();
        }
      }, 120000);
      
    } catch (error) {
      console.error('Error starting video recording:', error);
      alert('Failed to start video recording. Please ensure camera and microphone are accessible.');
    }
  };

  const stopVideoRecording = () => {
    if (videoMediaRecorderRef.current && isVideoRecording) {
      videoMediaRecorderRef.current.stop();
    }
  };

  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = () => {
          const base64Audio = reader.result;
          setAnswers(prev => ({
            ...prev,
            [questions[currentQuestion]._id]: {
              text: answers[questions[currentQuestion]._id]?.text || '',
              audio: base64Audio,
              audioUrl: audioUrl
            }
          }));
          setAnswerType('audio');
        };
        
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Please allow microphone access to record your answers.');
    }
  };

  const stopAudioRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
  };

  const saveTextAnswer = (text) => {
    setAnswers(prev => ({
      ...prev,
      [questions[currentQuestion]._id]: {
        text: text,
        audio: prev[questions[currentQuestion]._id]?.audio || null,
        audioUrl: prev[questions[currentQuestion]._id]?.audioUrl || null,
        video: null,
        videoUrl: null
      }
    }));
  };

  // ✅ Handle MCQ Answer Selection
  const handleMCQAnswer = (selectedOption) => {
    console.log(`MCQ Selected: ${selectedOption} for question ${questions[currentQuestion]._id}`);
    setAnswers(prev => ({
      ...prev,
      [questions[currentQuestion]._id]: {
        text: selectedOption,
        answerType: 'mcq'
      }
    }));
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      const nextQ = questions[currentQuestion + 1];
      if (nextQ?.type === 'video') {
        setAnswerType('video');
      } else if (nextQ?.type === 'mcq') {
        setAnswerType('mcq');
      } else {
        setAnswerType(answers[questions[currentQuestion + 1]?._id]?.audio ? 'audio' : 'text');
      }
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
      const prevQ = questions[currentQuestion - 1];
      if (prevQ?.type === 'video') {
        setAnswerType('video');
      } else if (prevQ?.type === 'mcq') {
        setAnswerType('mcq');
      } else {
        setAnswerType(answers[questions[currentQuestion - 1]?._id]?.audio ? 'audio' : 'text');
      }
    }
  };

  const handleSubmit = async () => {
    if (submitting || submitted) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/ai-interview/submit/${interviewId}`,
        {
          applicationId: applicationId,
          candidateEmail: candidateInfo.email,
          candidateName: candidateInfo.fullName,
          answers: answers,
          timeSpent: (questions.length * 120) - timeLeft,
          completedAt: new Date().toISOString(),
          proctoringData: {
            tabViolations: tabViolationCount,
            copyViolations: copyViolationCount,
            totalViolations: tabViolationCount + copyViolationCount
          }
        }
      );
      
      if (response.data.success) {
        setSubmitted(true);
        if (response.data.data?.evaluation) {
          setEvaluationResult(response.data.data.evaluation);
        }
        if (timerRef.current) clearInterval(timerRef.current);
        stopCamera();
      } else {
        alert(response.data.message || 'Failed to submit answers');
      }
    } catch (error) {
      console.error('Error submitting answers:', error);
      alert(error.response?.data?.message || 'Failed to submit answers. Please try again.');
    } finally {
      setSubmitting(false);
      setShowConfirmSubmit(false);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingCard}>
          <div style={styles.spinner}></div>
          <h3>Preparing Your AI Interview</h3>
          <p>Loading personalized questions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorCard}>
          <div style={styles.errorIcon}>⚠️</div>
          <h3>Unable to Load Interview</h3>
          <p>{error}</p>
          <button style={styles.primaryButton} onClick={onComplete}>Close</button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={styles.successContainer}>
        <div style={styles.successCard}>
          <div style={styles.successIcon}>✅</div>
          <h2>Interview Submitted Successfully!</h2>
          {evaluationResult && (
            <div style={styles.resultCard}>
              <div style={styles.scoreCircle}>
                <span style={styles.scoreNumber}>{evaluationResult.averageScore}%</span>
              </div>
              <p style={styles.scoreLabel}>Your Overall Score</p>
              <div style={styles.statusBadge}>
                {evaluationResult.isPassed ? '✅ Passed' : '❌ Failed'}
              </div>
              <p style={styles.scoreNote}>Passing Score Required: 60%</p>
              {answers.communication_video?.videoUrl && (
                <div style={styles.videoPreviewSection}>
                  <p>📹 Your Communication Assessment Video:</p>
                  <video controls src={answers.communication_video.videoUrl} style={styles.videoPreview} />
                </div>
              )}
            </div>
          )}
          <p style={styles.successMessage}>
            Thank you for completing the AI interview. Our team will review your responses and get back to you soon.
          </p>
          <button style={styles.primaryButton} onClick={onComplete}>Close</button>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorCard}>
          <p>No questions available for your position.</p>
          <button style={styles.primaryButton} onClick={onComplete}>Close</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const currentAnswer = answers[currentQ?._id]?.text || '';
  const hasAudio = !!answers[currentQ?._id]?.audio;
  const hasVideo = !!answers[currentQ?._id]?.video;
  const isVideoQuestion = currentQ?.type === 'video';
  const isMCQQuestion = currentQ?.type === 'mcq';
  const isLastQuestion = currentQuestion === questions.length - 1;
  const answeredCount = Object.keys(answers).filter(key => answers[key]?.text || answers[key]?.audio || answers[key]?.video).length;
  const progress = (answeredCount / questions.length) * 100;
  const totalViolations = tabViolationCount + copyViolationCount;
  const isCameraWorking = cameraActive && !cameraError;
  
  // Debug current question
  console.log(`Current question ${currentQuestion + 1}: Type=${currentQ?.type}, Is MCQ=${isMCQQuestion}, Has options=${!!currentQ?.options}`);

  return (
    <div style={styles.container}>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes wave { 0%, 100% { transform: scaleY(1); } 50% { transform: scaleY(2); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      ` }} />
      
      {/* Top Proctor Bar */}
      <div style={styles.proctorBar}>
        <div style={styles.proctorLeft}>
          <div style={styles.proctorIndicator}>
            <span style={{ ...styles.statusDot, backgroundColor: isCameraWorking ? '#10B981' : '#EF4444' }}></span>
            <span>Camera {isCameraWorking ? 'Active' : (cameraError ? 'Error' : 'Inactive')}</span>
          </div>
          <div style={styles.proctorIndicator}>
            <span style={{ ...styles.statusDot, backgroundColor: isFullscreen ? '#10B981' : '#F59E0B' }}></span>
            <span>Secure Mode {isFullscreen ? 'Active' : 'Inactive'}</span>
          </div>
          {totalViolations > 0 && (
            <div style={styles.proctorIndicator}>
              <span style={{ ...styles.statusDot, backgroundColor: '#F59E0B' }}></span>
              <span>Warnings: {fullscreenViolationCount}/{maxFullscreenViolations}</span>
            </div>
          )}
        </div>
        <button style={styles.fullscreenButton} onClick={enforceFullscreen}>
          🖥️ Fullscreen Mode
        </button>
      </div>

      {/* Lockdown Warning */}
      {showLockdownWarning && (
        <div style={styles.lockdownOverlay}>
          <div style={styles.lockdownModal}>
            <div style={styles.lockdownIcon}>⚠️</div>
            <p>{lockdownMessage}</p>
            <button style={styles.lockdownButton} onClick={() => setShowLockdownWarning(false)}>
              I Understand
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Left Panel - Question Area */}
        <div style={styles.questionPanel}>
          <div style={styles.header}>
            <div>
              <h1 style={styles.title}>AI Interview Assessment</h1>
              <p style={styles.subtitle}>{candidateInfo.fullName} • {candidateInfo.email}</p>
            </div>
            <div style={styles.timerCard}>
              <span style={styles.timerLabel}>Time Remaining</span>
              <span style={styles.timerValue}>{formatTime(timeLeft)}</span>
            </div>
          </div>

          <div style={styles.progressSection}>
            <div style={styles.progressInfo}>
              <span>Progress</span>
              <span>{answeredCount} of {questions.length} answered</span>
            </div>
            <div style={styles.progressBar}>
              <div style={{ ...styles.progressFill, width: `${progress}%` }} />
            </div>
          </div>

          <div style={styles.questionCard}>
            <div style={styles.questionMeta}>
              <span style={styles.questionNumber}>
                Question {currentQuestion + 1}
                {isVideoQuestion && <span style={styles.communicationBadge}>🎥 Communication Assessment</span>}
                {isMCQQuestion && <span style={styles.mcqBadge}>📝 Multiple Choice Question</span>}
              </span>
              <span style={styles.questionType}>
                {isVideoQuestion ? 'Video Response (2 min)' : (isMCQQuestion ? 'MCQ' : (currentQ.difficulty || 'Standard'))}
              </span>
            </div>
            <p style={styles.questionText}>{currentQ.question}</p>
          </div>

          <div style={styles.answerCard}>
            <div style={styles.answerHeader}>
              <span>Your Answer</span>
              {!isVideoQuestion && !isMCQQuestion && (
                <div style={styles.answerTypeToggle}>
                  <button 
                    style={{ ...styles.typeButton, ...(answerType === 'text' ? styles.typeActive : {}) }}
                    onClick={() => setAnswerType('text')}
                  >
                    📝 Type
                  </button>
                  <button 
                    style={{ ...styles.typeButton, ...(answerType === 'audio' ? styles.typeActive : {}) }}
                    onClick={() => setAnswerType('audio')}
                  >
                    🎤 Record
                  </button>
                </div>
              )}
            </div>

            {/* ✅ MCQ DISPLAY SECTION */}
            {isMCQQuestion ? (
              <div style={styles.mcqContainer}>
                {currentQ.options && Array.isArray(currentQ.options) && currentQ.options.length > 0 ? (
                  <div style={styles.mcqOptions}>
                    {currentQ.options.map((option, idx) => {
                      const isSelected = answers[currentQ._id]?.text === option;
                      return (
                        <div 
                          key={idx} 
                          style={styles.mcqOption(isSelected)} 
                          onClick={() => handleMCQAnswer(option)}
                        >
                          <div style={styles.mcqRadio(isSelected)}>
                            {isSelected && <div style={styles.mcqRadioInner} />}
                          </div>
                          <span style={styles.mcqOptionText}>{option}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={styles.mcqError}>
                    ⚠️ No options available for this MCQ question. 
                    <br />
                    <small>Question ID: {currentQ?._id}</small>
                    <br />
                    <small>Please contact support.</small>
                  </div>
                )}
              </div>
            ) : isVideoQuestion ? (
              <div style={styles.videoAnswerSection}>
                {!hasVideo ? (
                  <div style={styles.videoRecordSection}>
                    {!isVideoRecording ? (
                      <button style={styles.videoRecordButton} onClick={startVideoRecording}>
                        🎥 Start Video Recording (2 minutes)
                      </button>
                    ) : (
                      <div style={styles.videoRecordingSection}>
                        <div style={styles.videoRecordingIndicator}>
                          <span style={styles.recordingRedDot}></span>
                          Recording in progress...
                        </div>
                        <div style={styles.videoTimer}>
                          Time: {formatTime(videoDuration)} / 02:00
                        </div>
                        <div style={styles.videoProgressBar}>
                          <div style={{ ...styles.videoProgressFill, width: `${(videoDuration / 120) * 100}%` }} />
                        </div>
                        <button style={styles.videoStopButton} onClick={stopVideoRecording}>
                          ⏹️ Stop Recording
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={styles.videoPreview}>
                    <video controls src={answers.communication_video?.videoUrl} style={styles.videoPlayer} />
                    <button style={styles.rerecordButton} onClick={() => {
                      setAnswers(prev => {
                        const newAnswers = { ...prev };
                        delete newAnswers.communication_video;
                        return newAnswers;
                      });
                      setVideoRecordingComplete(false);
                    }}>
                      🔄 Re-record
                    </button>
                  </div>
                )}
              </div>
            ) : answerType === 'text' ? (
              <textarea
                style={styles.textarea}
                placeholder="Type your answer here..."
                value={currentAnswer}
                onChange={(e) => saveTextAnswer(e.target.value)}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
              />
            ) : (
              <div style={styles.audioSection}>
                {!hasAudio ? (
                  <div style={styles.recordSection}>
                    {!isRecording ? (
                      <button style={styles.recordButton} onClick={startAudioRecording}>
                        🎙️ Start Recording
                      </button>
                    ) : (
                      <div style={styles.recordingSection}>
                        <div style={styles.recordingWave}>
                          <span></span><span></span><span></span><span></span>
                        </div>
                        <button style={styles.stopButton} onClick={stopAudioRecording}>
                          ⏹️ Stop Recording
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={styles.audioPreview}>
                    <audio controls src={answers[currentQ._id]?.audioUrl} style={styles.audioPlayer} />
                    <button style={styles.rerecordButton} onClick={() => {
                      setAnswers(prev => {
                        const newAnswers = { ...prev };
                        delete newAnswers[currentQ._id];
                        return newAnswers;
                      });
                      setAnswerType('audio');
                    }}>
                      🔄 Record Again
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Camera Feed */}
        <div style={styles.cameraPanel}>
          <div style={styles.cameraCard}>
            <div style={styles.cameraHeader}>
              <span>Live Proctoring Feed</span>
              <span style={{ ...styles.liveBadge, color: isCameraWorking ? '#DC2626' : '#64748B' }}>
                ● {isCameraWorking ? 'LIVE' : (cameraError ? 'OFFLINE' : 'WAITING')}
              </span>
              {isVideoRecording && (
                <span style={styles.videoRecordingBadge}>🎥 RECORDING</span>
              )}
            </div>
            <div style={styles.videoWrapper}>
              {!isCameraWorking && (
                <div style={styles.cameraPlaceholder}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p>{cameraError || 'Camera not accessible'}</p>
                  <button style={styles.retryCameraButton} onClick={enableCamera}>
                    Try Again
                  </button>
                </div>
              )}
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                muted 
                style={{ ...styles.videoElement, display: isCameraWorking ? 'block' : 'none' }}
              />
            </div>
            <div style={styles.cameraFooter}>
              <p>You are being monitored • {isCameraWorking ? 'Recording Active' : 'Camera Required'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div style={styles.navigation}>
        <button 
          style={{ ...styles.navButton, ...(currentQuestion === 0 ? styles.disabledButton : {}) }}
          onClick={prevQuestion}
          disabled={currentQuestion === 0}
        >
          ← Previous Question
        </button>
        
        <div style={styles.navCenter}>
          <span style={styles.questionIndicator}>
            {currentQuestion + 1} / {questions.length}
          </span>
        </div>
        
        {isLastQuestion ? (
          (videoRecordingComplete || (!isVideoQuestion && !isMCQQuestion && answers[currentQ?._id]?.text) || (isMCQQuestion && answers[currentQ?._id]?.text)) ? (
            <button 
              style={styles.submitButton}
              onClick={() => setShowConfirmSubmit(true)}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Interview →'}
            </button>
          ) : (
            <button style={styles.disabledButton} disabled>
              {isMCQQuestion ? 'Please select an option to continue' : 'Complete this question to submit'}
            </button>
          )
        ) : (
          <button style={styles.navButton} onClick={nextQuestion}>
            Next Question →
          </button>
        )}
      </div>

      {/* Confirm Submit Modal */}
      {showConfirmSubmit && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalIcon}>📝</div>
            <h3>Submit Your Interview?</h3>
            <p>You have answered <strong>{answeredCount}</strong> out of <strong>{questions.length}</strong> questions.</p>
            {totalViolations > 0 && (
              <div style={styles.warningBox}>
                ⚠️ Proctoring Warnings: {fullscreenViolationCount}/{maxFullscreenViolations}
              </div>
            )}
            {answers.communication_video?.videoUrl && (
              <div style={styles.videoConfirmSection}>
                <p>📹 Your communication video has been recorded and will be submitted.</p>
              </div>
            )}
            <p style={styles.modalNote}>Once submitted, you cannot change your answers.</p>
            <div style={styles.modalButtons}>
              <button style={styles.cancelButton} onClick={() => setShowConfirmSubmit(false)}>
                Cancel
              </button>
              <button style={styles.confirmButton} onClick={handleSubmit}>
                Yes, Submit Interview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#F1F5F9',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    zIndex: 99999,
  },
  proctorBar: {
    backgroundColor: '#1E293B',
    padding: '12px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #334155',
  },
  proctorLeft: {
    display: 'flex',
    gap: '20px',
  },
  proctorIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#CBD5E1',
    fontSize: '12px',
    fontWeight: '500',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  fullscreenButton: {
    backgroundColor: '#3B82F6',
    color: 'white',
    border: 'none',
    padding: '6px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  mainContent: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 380px',
    gap: '24px',
    padding: '24px',
    overflow: 'auto',
  },
  questionPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  cameraPanel: {
    position: 'sticky',
    top: '24px',
    alignSelf: 'start',
  },
  header: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#0F172A',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748B',
    margin: '4px 0 0 0',
  },
  timerCard: {
    backgroundColor: '#FEF2F2',
    padding: '8px 16px',
    borderRadius: '8px',
    textAlign: 'center',
  },
  timerLabel: {
    fontSize: '11px',
    color: '#DC2626',
    display: 'block',
  },
  timerValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#DC2626',
    fontFamily: 'monospace',
  },
  progressSection: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '16px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  progressInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#475569',
    marginBottom: '8px',
  },
  progressBar: {
    height: '6px',
    backgroundColor: '#E2E8F0',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#3B82F6',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  questionCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '28px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  questionMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  questionNumber: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#3B82F6',
  },
  communicationBadge: {
    marginLeft: '10px',
    padding: '4px 10px',
    backgroundColor: '#FEF3C7',
    borderRadius: '20px',
    fontSize: '11px',
    color: '#D97706',
  },
  mcqBadge: {
    marginLeft: '10px',
    padding: '4px 10px',
    backgroundColor: '#E0E7FF',
    borderRadius: '20px',
    fontSize: '11px',
    color: '#4338CA',
  },
  questionType: {
    fontSize: '11px',
    padding: '4px 10px',
    backgroundColor: '#F1F5F9',
    borderRadius: '20px',
    color: '#475569',
  },
  questionText: {
    fontSize: '18px',
    lineHeight: '1.6',
    color: '#0F172A',
    fontWeight: '500',
    margin: 0,
  },
  answerCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  answerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#0F172A',
  },
  answerTypeToggle: {
    display: 'flex',
    gap: '8px',
  },
  typeButton: {
    padding: '6px 14px',
    backgroundColor: '#F1F5F9',
    border: '1px solid #E2E8F0',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  typeActive: {
    backgroundColor: '#3B82F6',
    color: 'white',
    borderColor: '#3B82F6',
  },
  textarea: {
    width: '100%',
    minHeight: '200px',
    padding: '14px',
    fontSize: '14px',
    lineHeight: '1.6',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  // ✅ MCQ Styles
  mcqContainer: {
    marginTop: '8px',
  },
  mcqOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  mcqOption: (isSelected) => ({
    padding: '14px 18px',
    borderRadius: '10px',
    border: `1.5px solid ${isSelected ? '#3B82F6' : '#E2E8F0'}`,
    backgroundColor: isSelected ? '#EFF6FF' : '#FFFFFF',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: isSelected ? '#EFF6FF' : '#F8FAFC',
      borderColor: '#3B82F6',
    }
  }),
  mcqRadio: (isSelected) => ({
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: `2px solid ${isSelected ? '#3B82F6' : '#CBD5E1'}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  mcqRadioInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#3B82F6',
  },
  mcqOptionText: {
    fontSize: '15px',
    color: '#0F172A',
    lineHeight: '1.4',
  },
  mcqError: {
    textAlign: 'center',
    padding: '30px',
    color: '#EF4444',
    backgroundColor: '#FEF2F2',
    borderRadius: '8px',
    fontSize: '14px',
  },
  videoAnswerSection: {
    minHeight: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoRecordSection: {
    textAlign: 'center',
  },
  videoRecordButton: {
    padding: '16px 32px',
    backgroundColor: '#DC2626',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    animation: 'pulse 2s infinite',
  },
  videoRecordingSection: {
    textAlign: 'center',
  },
  videoRecordingIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '16px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#DC2626',
  },
  recordingRedDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: '#DC2626',
    animation: 'pulse 1s infinite',
  },
  videoTimer: {
    fontSize: '24px',
    fontWeight: '700',
    fontFamily: 'monospace',
    marginBottom: '12px',
  },
  videoProgressBar: {
    width: '300px',
    height: '8px',
    backgroundColor: '#E2E8F0',
    borderRadius: '4px',
    overflow: 'hidden',
    margin: '16px auto',
  },
  videoProgressFill: {
    height: '100%',
    backgroundColor: '#DC2626',
    transition: 'width 1s linear',
  },
  videoStopButton: {
    padding: '10px 24px',
    backgroundColor: '#EF4444',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  videoPreview: {
    textAlign: 'center',
    width: '100%',
  },
  videoPlayer: {
    width: '100%',
    maxWidth: '500px',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  videoRecordingBadge: {
    backgroundColor: '#DC2626',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '10px',
    fontWeight: '600',
    animation: 'pulse 1s infinite',
  },
  videoPreviewSection: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#F1F5F9',
    borderRadius: '8px',
  },
  videoPreview: {
    width: '100%',
    maxWidth: '300px',
    borderRadius: '8px',
    marginTop: '8px',
  },
  videoConfirmSection: {
    margin: '12px 0',
    padding: '10px',
    backgroundColor: '#E8F5E9',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#2E7D32',
  },
  audioSection: {
    minHeight: '150px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  recordSection: {
    textAlign: 'center',
  },
  recordButton: {
    padding: '12px 28px',
    backgroundColor: '#3B82F6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  recordingSection: {
    textAlign: 'center',
  },
  recordingWave: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '16px',
  },
  stopButton: {
    padding: '10px 24px',
    backgroundColor: '#EF4444',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  audioPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    width: '100%',
  },
  audioPlayer: {
    flex: 1,
    minWidth: '200px',
  },
  rerecordButton: {
    padding: '8px 16px',
    backgroundColor: '#F1F5F9',
    border: '1px solid #E2E8F0',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  cameraCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  cameraHeader: {
    padding: '14px 18px',
    backgroundColor: '#F8FAFC',
    borderBottom: '1px solid #E2E8F0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: '500',
    color: '#0F172A',
  },
  liveBadge: {
    fontSize: '11px',
    fontWeight: '600',
  },
  videoWrapper: {
    backgroundColor: '#0F172A',
    aspectRatio: '4/3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  videoElement: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  cameraPlaceholder: {
    textAlign: 'center',
    color: '#94A3B8',
    padding: '20px',
  },
  retryCameraButton: {
    marginTop: '12px',
    padding: '8px 20px',
    backgroundColor: '#3B82F6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  cameraFooter: {
    padding: '10px',
    textAlign: 'center',
    fontSize: '11px',
    color: '#64748B',
    backgroundColor: '#F8FAFC',
    borderTop: '1px solid #E2E8F0',
  },
  navigation: {
    backgroundColor: 'white',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: '1px solid #E2E8F0',
  },
  navButton: {
    padding: '10px 24px',
    backgroundColor: 'white',
    border: '1px solid #CBD5E1',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  disabledButton: {
    padding: '10px 24px',
    backgroundColor: '#E2E8F0',
    border: '1px solid #CBD5E1',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#94A3B8',
    cursor: 'not-allowed',
  },
  navCenter: {
    fontSize: '13px',
    color: '#64748B',
  },
  questionIndicator: {
    fontWeight: '500',
  },
  submitButton: {
    padding: '10px 28px',
    backgroundColor: '#10B981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '420px',
    width: '90%',
    textAlign: 'center',
  },
  modalIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  warningBox: {
    backgroundColor: '#FEF3C7',
    padding: '10px',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#D97706',
    margin: '12px 0',
  },
  modalNote: {
    fontSize: '13px',
    color: '#64748B',
    marginTop: '12px',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
    justifyContent: 'center',
  },
  cancelButton: {
    padding: '10px 24px',
    backgroundColor: '#F1F5F9',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  confirmButton: {
    padding: '10px 24px',
    backgroundColor: '#10B981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  lockdownOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 300000,
  },
  lockdownModal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '32px',
    textAlign: 'center',
    maxWidth: '380px',
  },
  lockdownIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  lockdownButton: {
    marginTop: '20px',
    padding: '10px 24px',
    backgroundColor: '#3B82F6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  loadingContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#F1F5F9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingCard: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '16px',
    textAlign: 'center',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #E2E8F0',
    borderTop: '3px solid #3B82F6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  successContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#F1F5F9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  successCard: {
    backgroundColor: 'white',
    padding: '48px',
    borderRadius: '20px',
    textAlign: 'center',
    maxWidth: '500px',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
  },
  successIcon: {
    fontSize: '64px',
    marginBottom: '20px',
  },
  resultCard: {
    backgroundColor: '#F8FAFC',
    padding: '24px',
    borderRadius: '12px',
    margin: '24px 0',
  },
  scoreCircle: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    backgroundColor: '#10B981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  scoreNumber: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: 'white',
  },
  scoreLabel: {
    fontSize: '14px',
    color: '#475569',
    marginBottom: '8px',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    margin: '8px 0',
  },
  scoreNote: {
    fontSize: '12px',
    color: '#64748B',
    marginTop: '8px',
  },
  successMessage: {
    color: '#475569',
    lineHeight: '1.6',
  },
  errorContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#F1F5F9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorCard: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '16px',
    textAlign: 'center',
    maxWidth: '400px',
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  primaryButton: {
    marginTop: '24px',
    padding: '10px 28px',
    backgroundColor: '#3B82F6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};

export default AIInterview;